/**
 * Activity Addition Form Controller (EPIC-027 / 27.5)
 *
 * Left column of /simulateur-ajout-activite.html (§17.B). Manages:
 *  - Initial pre-fill from GET /simulator/add-activity/prefill (403 → /).
 *  - Existing-activities cards built from `existing-activity-tpl`.
 *  - Conditional visibility (RC Pro, ACRE, CA monthly inputs).
 *  - Horizon toggle 12 / 24 months.
 *  - Debounced (400 ms) POST /simulator/add-activity/calculate.
 *  - Inline 422 errors under fields via `[data-error-for]`.
 *  - sessionStorage draft persistence under `simulator.add-activity.draft.v1`.
 *  - Cross-controller dispatch to the right-column results controller.
 */

import BaseController from "./base_controller.js";
import { api } from "../services/api.js";
import { debounce } from "../utils/debounce.js";
import {
  serializeActivityAdditionForm,
  hydrateActivityAdditionForm,
} from "../utils/activity_addition_serializer.js";

const DRAFT_KEY = "simulator.add-activity.draft.v1";
const REGULATED_DEFAULTS = {
  droit: 250,
  sante: 300,
  batiment: 1200,
  immobilier: 300,
  autre: 150,
  taxi: 150,
};

export default class ActivityAdditionFormController extends BaseController {
  static targets = [
    "formColumn",
    "defaultActivityNotice",
    "existingActivitiesFieldset",
    "existingActivitiesList",
    "newActivityFieldset",
    "newCategory",
    "regulated",
    "rcProConditional",
    "fiscalOptions",
    "vlOptionYes",
    "vlOptionNo",
    "acreLeftBlock",
    "horizonFieldset",
    "caLinearWrap",
    "caMonthlyWrap",
  ];

  connect() {
    this.debouncedCalculate = debounce(() => this.calculate(), 400);
    this.lastPrefill = null;

    this.bootstrap();
  }

  disconnect() {
    if (this.debouncedCalculate) this.debouncedCalculate.cancel();
  }

  async bootstrap() {
    try {
      const res = await api.get("/simulator/add-activity/prefill");
      const prefill = res && (res.data || res);
      this.lastPrefill = prefill;
      this.applyPrefill(prefill);
    } catch (err) {
      if (err && err.status === 403) {
        this.handleAccountInactive();
        return;
      }
      this.showError("Impossible de charger les données du simulateur. Réessayez.");
    }

    this.restoreDraft();
    this.applyConditionalVisibility();
    this.calculate();
  }

  handleAccountInactive() {
    this.showError(
      "Ce simulateur est réservé aux micro-entrepreneurs actifs.",
    );
    window.location.assign("/");
  }

  // ---------------------------------------------------------------------------
  // Pre-fill
  // ---------------------------------------------------------------------------

  applyPrefill(prefill) {
    if (!prefill || typeof prefill !== "object") return;

    this.renderExistingActivities(prefill.existingActivities || []);
    this.applyGlobalOptions(prefill.globalOptions || {});
    this.applyOrphanNotice(prefill.orphanRevenueReportedOnPrincipal === true);

    const simDate = this.element.querySelector('[data-field="simulationDate"]');
    if (simDate) simDate.value = prefill.simulationDate || "";
  }

  renderExistingActivities(rows) {
    if (!this.hasExistingActivitiesListTarget) return;
    this.existingActivitiesListTarget.replaceChildren();

    if (rows.length === 0) {
      const empty = document.createElement("p");
      empty.className = "fs-13 text-muted";
      empty.textContent =
        "Vous n'avez pas encore d'activité enregistrée. La projection partira d'un CA initial nul.";
      this.existingActivitiesListTarget.append(empty);
      return;
    }

    rows.forEach((row) => {
      const fragment = this.cloneTemplate("existing-activity-tpl");
      const article = fragment.querySelector("[data-activity-role='existing']");
      if (article) {
        article.dataset.activityId = row.activityId || "";
        article.dataset.regime = row.regime || "bic_services";
        article.dataset.description = row.description || "";
        article.dataset.realCa12mEur = String(row.realCa12mEur ?? 0);
      }
      this.fillSlots(fragment, {
        regimeLabel: regimeLabel(row.regime),
        categoryCode: (row.regime || "").toUpperCase(),
        description: row.description || "—",
        caReal: formatEuro(row.realCa12mEur),
      });

      const growthInput = fragment.querySelector('[data-slot="growthInput"]');
      if (growthInput) {
        if (row.evolutionRateSurcharge !== null && row.evolutionRateSurcharge !== undefined) {
          growthInput.value = String(Math.round(row.evolutionRateSurcharge * 1000) / 10);
        }
        growthInput.addEventListener("input", () => this.onInputChange());
      }
      this.existingActivitiesListTarget.append(fragment);
    });
  }

  applyGlobalOptions(opts) {
    const setField = (name, value) => {
      const el = this.element.querySelector(`[data-field="${name}"]`);
      if (el && value !== undefined && value !== null) el.value = String(value);
    };

    setField("vlOption", opts.vlOption ? "true" : "false");
    setField("fiscalShares", opts.fiscalShares ?? 1);
    setField("referenceFiscalIncomeN2", opts.referenceFiscalIncomeN2Eur ?? 0);
    setField("acreEligible", opts.acreEligible ? "true" : "false");
    setField("acreStartDate", opts.acreStartDate ? toDateOnly(opts.acreStartDate) : "");
    setField("acreEndDate", opts.acreEndDate ? toDateOnly(opts.acreEndDate) : "");

    if (opts.marginalTaxRate !== null && opts.marginalTaxRate !== undefined) {
      const tmiPct = Math.round(opts.marginalTaxRate * 100);
      setField("tmi", tmiPct);
      const tmiSlot = this.element.querySelector('[data-slot="tmiLabel"]');
      if (tmiSlot) {
        tmiSlot.textContent = `${tmiPct} % — tranche marginale d'imposition`;
      }
    } else {
      setField("tmi", 11);
    }

    if (this.hasVlOptionYesTarget && this.hasVlOptionNoTarget) {
      const yes = opts.vlOption === true;
      this.vlOptionYesTarget.setAttribute("aria-pressed", yes ? "true" : "false");
      this.vlOptionYesTarget.classList.toggle("nm-btn--solid", yes);
      this.vlOptionYesTarget.classList.toggle("nm-btn--ghost", !yes);
      this.vlOptionNoTarget.setAttribute("aria-pressed", yes ? "false" : "true");
      this.vlOptionNoTarget.classList.toggle("nm-btn--solid", !yes);
      this.vlOptionNoTarget.classList.toggle("nm-btn--ghost", yes);
    }

    if (this.hasAcreLeftBlockTarget) {
      this.acreLeftBlockTarget.hidden = !opts.acreEligible;
      const endSlot = this.acreLeftBlockTarget.querySelector('[data-slot="acreEndLabel"]');
      if (endSlot && opts.acreEndDate) {
        endSlot.textContent = formatFrDate(opts.acreEndDate);
      }
    }
  }

  applyOrphanNotice(hasOrphan) {
    if (!this.hasDefaultActivityNoticeTarget) return;
    this.defaultActivityNoticeTarget.hidden = !hasOrphan;
  }

  // ---------------------------------------------------------------------------
  // Event handlers
  // ---------------------------------------------------------------------------

  onInputChange() {
    this.applyConditionalVisibility();
    this.persistDraft();
    this.debouncedCalculate();
  }

  onRegulatedChange() {
    const selected = this.hasRegulatedTarget ? this.regulatedTarget.value : "none";
    const rcProInput = this.element.querySelector("#new-rc-pro");
    if (rcProInput && selected !== "none" && Object.prototype.hasOwnProperty.call(REGULATED_DEFAULTS, selected)) {
      rcProInput.value = String(REGULATED_DEFAULTS[selected]);
    }
  }

  setCaMode(event) {
    const btn = event.currentTarget;
    const mode = btn.dataset.inputMode === "monthly" ? "monthly" : "linear";
    const buttons = this.element.querySelectorAll("[data-input-mode]");
    buttons.forEach((b) => {
      const matches = b === btn;
      b.setAttribute("aria-pressed", matches ? "true" : "false");
      b.classList.toggle("nm-btn--solid", matches);
      b.classList.toggle("nm-btn--ghost", !matches);
    });
    this.applyCaMode(mode);
    this.onInputChange();
  }

  applyCaMode(mode) {
    if (!this.hasCaLinearWrapTarget || !this.hasCaMonthlyWrapTarget) return;
    if (mode === "monthly") {
      this.caLinearWrapTarget.hidden = true;
      this.caMonthlyWrapTarget.hidden = false;
      this.ensureMonthlyInputs();
    } else {
      this.caLinearWrapTarget.hidden = false;
      this.caMonthlyWrapTarget.hidden = true;
    }
  }

  ensureMonthlyInputs() {
    if (this.caMonthlyWrapTarget.children.length > 0) return;
    const monthLabels = ["M+1", "M+2", "M+3", "M+4", "M+5", "M+6", "M+7", "M+8", "M+9", "M+10", "M+11", "M+12"];
    const ca12m = parseLooseNumber(this.element.querySelector("#new-ca-12m")?.value, 0);
    const monthly = ca12m > 0 ? Math.round((ca12m / 12) * 100) / 100 : 0;

    monthLabels.forEach((label, idx) => {
      const wrap = document.createElement("div");
      wrap.className = "nm-field";
      wrap.style.marginTop = idx === 0 ? "0" : "8px";

      const lbl = document.createElement("label");
      lbl.className = "nm-field__label";
      lbl.textContent = `CA mois ${label}`;
      wrap.append(lbl);

      const row = document.createElement("div");
      row.className = "flex items-center gap-12";

      const input = document.createElement("input");
      input.className = "nm-input nm-input--mono";
      input.type = "text";
      input.value = monthly > 0 ? String(monthly) : "";
      input.dataset.monthlyCaInput = "true";
      input.dataset.monthIndex = String(idx);
      input.style.textAlign = "right";
      input.addEventListener("input", () => this.onInputChange());

      const unit = document.createElement("span");
      unit.className = "fs-14 text-muted";
      unit.style.fontFamily = "var(--mono)";
      unit.textContent = "€ HT";

      row.append(input, unit);
      wrap.append(row);
      this.caMonthlyWrapTarget.append(wrap);
    });
  }

  setHorizon(event) {
    const btn = event.currentTarget;
    const months = btn.dataset.horizon === "24" ? "24" : "12";
    if (this.hasHorizonFieldsetTarget) {
      const buttons = this.horizonFieldsetTarget.querySelectorAll("[data-horizon]");
      buttons.forEach((b) => {
        const matches = b === btn;
        b.setAttribute("aria-pressed", matches ? "true" : "false");
        b.classList.toggle("nm-btn--solid", matches);
        b.classList.toggle("nm-btn--ghost", !matches);
      });
    }
    btn.dataset.horizon = months;
    this.onInputChange();
  }

  applyConditionalVisibility() {
    if (this.hasRegulatedTarget && this.hasRcProConditionalTarget) {
      const v = this.regulatedTarget.value;
      this.rcProConditionalTarget.hidden = !v || v === "none";
    }
  }

  // ---------------------------------------------------------------------------
  // Persistence
  // ---------------------------------------------------------------------------

  persistDraft() {
    try {
      const state = serializeActivityAdditionForm(this.element);
      sessionStorage.setItem(DRAFT_KEY, JSON.stringify(state));
    } catch (_e) {
      // sessionStorage unavailable (private mode) — silent skip.
    }
  }

  restoreDraft() {
    try {
      const raw = sessionStorage.getItem(DRAFT_KEY);
      if (!raw) return;
      const state = JSON.parse(raw);
      hydrateActivityAdditionForm(this.element, state);
    } catch (_e) {
      // corrupted draft — ignore.
    }
  }

  // ---------------------------------------------------------------------------
  // Calculate
  // ---------------------------------------------------------------------------

  async calculate() {
    this.clearFieldErrors();
    const body = serializeActivityAdditionForm(this.element);
    this.setBusy(true);

    try {
      const res = await api.post("/simulator/add-activity/calculate", body);
      const payload = (res && (res.data || res)) || null;
      this.dispatchResults(payload, body);
    } catch (err) {
      if (err && err.status === 422 && err.data && err.data.error) {
        this.applyFieldErrors(err.data.error);
      } else if (err && err.status === 403) {
        this.handleAccountInactive();
      } else if (err && err.status === 401) {
        // Centralized handling in api.js
      } else {
        this.showError("Le calcul du simulateur a échoué. Réessayez.");
      }
    } finally {
      this.setBusy(false);
    }
  }

  setBusy(busy) {
    const resultsRoot = document.querySelector(
      '[data-activity-addition-results-target="resultsColumn"]',
    );
    if (resultsRoot) {
      resultsRoot.setAttribute("aria-busy", busy ? "true" : "false");
    }
  }

  dispatchResults(payload, formState) {
    if (!payload) return;
    const resultsRoot = document.querySelector(
      '[data-activity-addition-results-target="resultsColumn"]',
    );
    if (!resultsRoot) return;
    const ctrl = this.application.getControllerForElementAndIdentifier(
      resultsRoot,
      "activity-addition-results",
    );
    if (ctrl && typeof ctrl.render === "function") {
      ctrl.render(payload, formState);
    }
  }

  applyFieldErrors(errorBlock) {
    const fields = (errorBlock.details && errorBlock.details.fields) || [];
    fields.forEach((f) => {
      const slot = this.element.querySelector(`[data-error-for="${cssEscape(f.field)}"]`);
      if (slot) {
        slot.textContent = f.message || "Valeur invalide.";
        slot.hidden = false;
      }
    });
  }

  clearFieldErrors() {
    this.element.querySelectorAll("[data-error-for]").forEach((slot) => {
      slot.textContent = "";
      slot.hidden = true;
    });
  }

  // ---------------------------------------------------------------------------
  // Exposed for scenario-save controller
  // ---------------------------------------------------------------------------

  getInputsForScenarioSave() {
    return serializeActivityAdditionForm(this.element);
  }

  getLastResult() {
    const resultsRoot = document.querySelector(
      '[data-activity-addition-results-target="resultsColumn"]',
    );
    if (!resultsRoot) return null;
    const ctrl = this.application.getControllerForElementAndIdentifier(
      resultsRoot,
      "activity-addition-results",
    );
    return ctrl && typeof ctrl.getLastPayload === "function" ? ctrl.getLastPayload() : null;
  }
}

function regimeLabel(value) {
  switch (value) {
    case "bic_vente": return "BIC vente";
    case "bic_services": return "BIC services";
    case "bnc": return "BNC";
    default: return value || "—";
  }
}

function formatEuro(amount) {
  if (amount === null || amount === undefined) return "—";
  const n = Number(amount);
  if (Number.isNaN(n)) return "—";
  return `${n.toLocaleString("fr-FR", { maximumFractionDigits: 0 })} €`;
}

function formatFrDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("fr-FR");
}

function toDateOnly(iso) {
  if (!iso) return "";
  return String(iso).slice(0, 10);
}

function parseLooseNumber(raw, defaultValue) {
  if (raw === null || raw === undefined) return defaultValue;
  const cleaned = String(raw).replace(/[\s  ]/g, "").replace(",", ".");
  const n = Number.parseFloat(cleaned);
  return Number.isNaN(n) ? defaultValue : n;
}

function cssEscape(value) {
  if (window.CSS && typeof window.CSS.escape === "function") {
    return window.CSS.escape(value);
  }
  return String(value).replace(/([!"#$%&'()*+,./:;<=>?@[\]^`{|}~])/g, "\\$1");
}
